import { Modal } from "@/components/renderbus-modal";
import { Form, Input, Select } from "@renderbus/ui";
import { FormInstance, FormItemProps, FormProps, ModalProps } from "antd";
import { ValidateCodeItem } from "@/pages/profile/components/action-modal/change-account-modal/components/validate-code-item";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import { isEn } from "project-config/utils/env";
import { useAreaCodeOptions } from "./hooks/useAreaCodeOptions";
import { useWatch } from "antd/es/form/Form";

export function ChangeAccountModal({
  title,
  open,
  form,
  labelSpan,
  concatLabel,
  concatPlaceholder,
  concatRules,
  validateLabel,
  formProps,
  modalProps,
  codePlaceholder,
  sendCode,
  sendVoiceCode,
  successTip,
  sendCodeDisable,
  countDown,
  sending,
  extra,
  sendCodeText,
  isForeignPhone,
}: {
  title: string;
  open?: boolean;
  form?: FormInstance;
  labelSpan?: number;
  changeOpen?: (open?: boolean) => void;
  concatLabel?: string;
  concatPlaceholder?: string;
  concatRules?: FormItemProps["rules"];
  validateLabel?: string;
  formProps?: FormProps;
  modalProps?: ModalProps;
  codePlaceholder?: string;
  sendCode?: () => void;
  sendVoiceCode?: () => void;
  successTip?: string;
  sendCodeDisable?: boolean;
  countDown?: number;
  sending?: boolean;
  extra?: JSX.Element;
  sendCodeText?: string;
  isForeignPhone?: boolean;
}) {
  const { t } = useTranslation("profile");
  const { t: centerT } = useTranslation("center");
  const m1 = t("please_enter_the_verification_code");
  const m2 = t("verification_code_error");
  const { areaCodeOptions } = useAreaCodeOptions();
  const areaCode = useWatch("areaCode", form);

  return (
    <Modal
      title={title}
      open={open}
      {...modalProps}
      width={isEn() ? "630px" : "max-content"}
    >
      <FormWrapper>
        <Form
          form={form}
          size={"large"}
          labelCol={{ span: labelSpan }}
          labelAlign="left"
          colon={false}
          requiredMark={false}
          {...formProps}
        >
          <Form.Item
            label={concatLabel}
            name=""
            rules={
              isForeignPhone
                ? [
                    {
                      validator: (_, value) => {
                        if (!areaCode)
                          return Promise.reject(
                            centerT("utils_validate_tel_area_phone")
                          );
                        return Promise.resolve();
                      },
                    },
                  ]
                : []
            }
          >
            <Row>
              {isForeignPhone && (
                <Form.Item
                  name="areaCode"
                  className="arear-code-form-item"
                  style={{ marginBottom: "0" }}
                >
                  <Select
                    popupClassName="area-code-select-panel"
                    allowClear={false}
                    placeholder={centerT("Select")}
                    onChange={(value) => {
                      form?.setFieldValue("areaCode", value);
                      form?.validateFields();
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children?.toString() ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {areaCodeOptions?.map((item, index) => (
                      <Select.Option value={item.value} key={String(index)}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                  <AreaCodeSelectStyle />
                </Form.Item>
              )}
              <Form.Item
                name="concat"
                rules={!isForeignPhone ? concatRules : []}
                style={{ width: "100%", marginBottom: "0" }}
              >
                <Input placeholder={concatPlaceholder} />
              </Form.Item>
            </Row>
          </Form.Item>
          <Form.Item
            name="code"
            label={validateLabel}
            rules={[
              {
                required: true,
                message: m1,
              },
              { pattern: /^\d{6}$/, message: m2 },
            ]}
          >
            <ValidateCodeItem
              codePlaceholder={codePlaceholder}
              countDown={countDown}
              sendCode={sendCode}
              sendVoiceCode={sendVoiceCode}
              sendCodeDisable={sendCodeDisable}
              sending={sending}
              sendCodeText={sendCodeText}
            />
          </Form.Item>
          <ExtraWrapper>
            <div className={`ant-col-${labelSpan}`} style={{ height: 1 }}></div>
            {extra}
          </ExtraWrapper>
          <div>{successTip}</div>
        </Form>
      </FormWrapper>
    </Modal>
  );
}

const AreaCodeSelectStyle = createGlobalStyle`
  .area-code-select-panel {
    width: 280px !important;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  input {
    height: 40px;
  }
`;

const ExtraWrapper = styled.div`
  display: flex;
`;

const FormWrapper = styled.div`
  .ant-input {
    font-size: 14px;
  }
  .ant-btn {
    font-size: 14px;
  }
  .renderbus-ui--input {
    background: #2c2c31 !important;
  }
  .arear-code-form-item {
    .ant-select-selector {
      background: #2c2c31 !important;
    }
    .ant-form-item-control {
      max-width: 100px;
    }
    .renderbus-ui--select {
      min-width: 100px;
      max-width: 100px;
      font-size: 14px;
    }
  }
`;
